import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  XCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import {
  formatPipelineJson,
  scriptAudioAlignStepLabel,
} from '@/lib/scriptAudioAlignPipeline';

function StatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (status === 'failed') return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
  if (status === 'running') return <Loader2 className="w-4 h-4 text-amber-400 animate-spin shrink-0" />;
  return <span className="w-4 h-4 rounded-full border border-slate-500 shrink-0" />;
}

function IoPanel({ label, data, tone = 'slate' }) {
  const [copied, setCopied] = useState(false);
  const text = formatPipelineJson(data);
  const toneClass = tone === 'emerald'
    ? 'border-emerald-500/25 bg-emerald-950/20'
    : 'border-slate-700/60 bg-slate-950/50';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={cn('rounded-lg border overflow-hidden', toneClass)}>
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-white/5">
        <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
        <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={handleCopy}>
          <Copy className="w-3 h-3 mr-1" />
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="text-[11px] text-slate-300 whitespace-pre-wrap overflow-x-auto p-3 max-h-48 overflow-y-auto font-mono">
        {text}
      </pre>
    </div>
  );
}

function PipelineStepRow({ step, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const title = step?.title || scriptAudioAlignStepLabel(step);
  const hasIo = Boolean(step?.input && Object.keys(step.input).length)
    || Boolean(step?.output && Object.keys(step.output).length);

  return (
    <li className="rounded-lg border border-slate-700/70 bg-slate-900/40 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-slate-800/40"
        onClick={() => hasIo && setOpen((v) => !v)}
        disabled={!hasIo}
      >
        <StatusIcon status={step?.status} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-100">{title}</span>
            <span className="text-[10px] font-mono text-violet-300/80">{step?.module}</span>
          </div>
          {step?.error ? (
            <p className="text-xs text-red-400 mt-0.5">{step.error}</p>
          ) : null}
        </div>
        {hasIo ? (
          open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        ) : null}
      </button>
      {open && hasIo ? (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-700/50 pt-2">
          {step?.input && Object.keys(step.input).length > 0 ? (
            <IoPanel label="Input" data={step.input} />
          ) : null}
          {step?.output && Object.keys(step.output).length > 0 ? (
            <IoPanel label="Output" data={step.output} tone="emerald" />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function ScriptAudioAlignPipelineDialog({
  open,
  onOpenChange,
  result,
  loading = false,
  error = null,
}) {
  const steps = Array.isArray(result?.pipeline_steps) ? result.pipeline_steps : [];
  const progress = useMemo(() => {
    if (!steps.length) return loading ? 8 : 0;
    const terminal = steps.filter((s) => s.status === 'completed' || s.status === 'failed').length;
    return Math.round((terminal / steps.length) * 100);
  }, [steps, loading]);

  const isComplete = result?.status === 'completed';
  const isRunning = result?.status === 'running' || loading;
  const isFailed = result?.status === 'failed' || Boolean(error);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-950 border-slate-800 text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            {isRunning && !isComplete ? (
              <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
            ) : isComplete ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : isFailed ? (
              <XCircle className="w-5 h-5 text-red-400" />
            ) : (
              <Loader2 className="w-5 h-5 text-slate-500" />
            )}
            Script → audio alignment pipeline
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Each step shows its module, input payload, and output payload.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          <div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-slate-500 mt-1">
              {isRunning && !isComplete ? 'Running pipeline…' : isComplete ? 'Pipeline complete' : isFailed ? 'Pipeline failed' : 'Ready'}
              {steps.length
                ? ` · ${steps.filter((s) => s.status === 'completed').length}/${steps.length} done`
                : ''}
            </p>
          </div>

          {(error || result?.error_message) && (
            <div className="rounded-lg border border-red-500/30 bg-red-950/30 p-3 text-sm text-red-300">
              {error || result?.error_message}
            </div>
          )}

          {result?.audio_url && (
            <div className="rounded-lg border border-violet-500/25 bg-violet-950/20 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-300">Master audio</p>
              <audio controls className="w-full h-9" preload="metadata" src={mediaUrlForPlayback(result.audio_url)} />
              <p className="text-[11px] text-slate-400">
                Duration: {Number(result.duration_seconds || 0).toFixed(2)}s
                {' · '}
                Alignment: {result.alignment_source || '—'}
              </p>
            </div>
          )}

          {steps.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pipeline steps</h3>
              <ul className="space-y-2">
                {steps.map((step) => (
                  <PipelineStepRow
                    key={`${step.step_id}-${step.started_at || step.title}`}
                    step={step}
                    defaultOpen={step.step_id?.startsWith('align_words_') || step.step_id === 'concat_audio' || step.step_id === 'build_timeline'}
                  />
                ))}
              </ul>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Processing…
            </div>
          ) : null}

          {isComplete && Array.isArray(result?.sentences) && result.sentences.length > 0 ? (
            <div className="rounded-lg border border-slate-700/60 bg-slate-900/30 p-3 space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Timeline summary</h3>
              <ul className="space-y-2 max-h-48 overflow-y-auto text-xs text-slate-300">
                {result.sentences.map((s) => (
                  <li key={s.index} className="font-mono space-y-1">
                    <div>
                      [{Number(s.start_time).toFixed(2)}s – {Number(s.end_time).toFixed(2)}s]
                      {' '}
                      {s.text}
                      {' '}
                      <span className="text-slate-500">({(s.words || []).length} words)</span>
                    </div>
                    {(s.words || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1 pl-2">
                        {s.words.map((w, wi) => (
                          <span
                            key={`${s.index}-${wi}-${w.token}`}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-violet-200"
                            title={`${Number(w.global_start).toFixed(2)}s – ${Number(w.global_end).toFixed(2)}s`}
                          >
                            {w.word}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
